import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { api, throwIfNotOk } from '../lib/api';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { conditionLabel } from '@siliconx/shared/ui/ConditionBadge';
import { formatDate } from '../lib/format';
import { formatDeliveryWindow } from '../lib/dates';
import { useAuth } from '../lib/authContext';
import { ProfileLockedCard } from '../components/ProfileLockedCard';
import type { DirectOfferListItem } from '@siliconx/shared/dto/direct-offer';
import type { DirectOffersResponse } from '../lib/types';
import { qk } from '../lib/queryKeys';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { DataTable } from '@siliconx/shared/ui/DataTable';

export const Route = createFileRoute('/direct-offers')({
  component: DirectOffersPage,
});

// A direct_offer is the buyer's DEMAND-driven counterpart to the per-listing
// `offer` on /offers: not a bid against one listing/seller, but a standing
// request against an anonymized (SKU, condition, region) bucket that fills as
// opted-in sellers accept units against it (open/filled/withdrawn). Split out
// of /offers (which is per-listing offers only) so each funnel has its own
// surface — the seller-facing counterpart is /opportunities (received
// direct offers a seller can fill).
// SIL-223: server-resolved readable SKU name + aggregate fill count — the
// list endpoint's shape (DirectOfferListItem), not the bare direct_offer row.
type DirectOffer = DirectOfferListItem;

const DIRECT_OFFER_STATUS_STYLES: Record<string, string> = {
  open: 'bg-amber-100 text-amber-700',
  filled: 'bg-green-100 text-green-700',
  // 670: expiry is a neutral outcome (zinc), not an error (red).
  expired: 'bg-zinc-100 text-zinc-500',
  withdrawn: 'bg-zinc-100 text-zinc-700',
};

function DirectOffersPage() {
  const { canBuy, buyerMissingFields } = useAuth();
  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery<DirectOffersResponse>({
    queryKey: qk.directOffers(),
    queryFn: async () => {
      const res = await api.api.client['direct-offers'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as DirectOffersResponse;
    },
    enabled: canBuy,
  });

  const withdraw = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.api.client['direct-offers'][':id'].withdraw.$post({ param: { id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Direct offer withdrawn.');
      void queryClient.invalidateQueries({ queryKey: qk.directOffers() });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Withdraw failed.'),
  });

  const rows = data?.data ?? [];

  const columns: ColumnDef<DirectOffer, unknown>[] = [
    {
      id: 'sku',
      header: 'SKU',
      cell: (ctx) => {
        const o = ctx.row.original;
        return (
          <span className="text-zinc-700">
            {o.skuShortDescription ?? (
              <span className="font-mono text-xs text-zinc-400">{o.skuId.slice(0, 8)}</span>
            )}
          </span>
        );
      },
    },
    {
      id: 'conditionBand',
      header: 'Condition',
      cell: (ctx) => (
        <span className="text-zinc-700">{conditionLabel(ctx.row.original.conditionBand)}</span>
      ),
    },
    {
      id: 'regionId',
      header: 'Region',
      cell: (ctx) => <span className="text-zinc-500">{ctx.row.original.regionId ?? 'Any'}</span>,
    },
    {
      id: 'pricePerUnitUsd',
      header: 'Price/unit',
      cell: (ctx) => (
        <div className="text-right font-medium text-zinc-900">
          <UsdCell value={ctx.row.original.pricePerUnitUsd} />
        </div>
      ),
    },
    {
      id: 'fillProgress',
      header: 'Fill progress',
      cell: (ctx) => (
        <div className="text-right text-zinc-700">
          {ctx.row.original.quantityFilled} / {ctx.row.original.quantityDesired}
        </div>
      ),
    },
    {
      id: 'deliveryWindow',
      header: 'Delivery window',
      cell: (ctx) => (
        <span className="text-zinc-500">
          {formatDeliveryWindow(ctx.row.original.deliverFrom, ctx.row.original.deliverBy)}
        </span>
      ),
    },
    {
      id: 'expiresAt',
      header: 'Expires',
      cell: (ctx) => (
        <span className="text-zinc-500">
          {ctx.row.original.expiresAt ? formatDate(ctx.row.original.expiresAt) : '—'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (ctx) => {
        const status = ctx.row.original.status;
        return (
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
              DIRECT_OFFER_STATUS_STYLES[status] ?? 'bg-zinc-100 text-zinc-700'
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Submitted',
      cell: (ctx) => (
        <span className="text-zinc-500">{formatDate(ctx.row.original.createdAt)}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (ctx) => {
        const o = ctx.row.original;
        return (
          <div className="text-right">
            {o.status === 'open' && (
              <button
                type="button"
                onClick={() => withdraw.mutate(o.id)}
                disabled={withdraw.isPending}
                className="rounded border border-zinc-300 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              >
                {withdraw.isPending ? '…' : 'Withdraw'}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Direct Offers" pinTo="/direct-offers" />
      <p className="text-sm text-zinc-500">
        Standing offers you&rsquo;ve placed on anonymized inventory buckets on the{' '}
        <Link to="/inventory" className="font-medium text-brand-600 hover:underline">
          Browse Listings
        </Link>{' '}
        page — targeted offers to owners of matching unlisted hardware. A direct offer fills as
        opted-in sellers accept units against it — sellers stay anonymous until fill. To bid on a specific listed unit instead, see your{' '}
        <Link to="/offers" className="font-medium text-brand-600 hover:underline">
          offers
        </Link>
        .
      </p>

      {!canBuy ? (
        <ProfileLockedCard side="buyer" missingFields={buyerMissingFields} />
      ) : rows.length === 0 ? (
        <Card className="text-center text-sm text-zinc-400">
          {isFetching ? 'Loading…' : 'No direct offers yet.'}
        </Card>
      ) : (
        <DataTable data={rows} columns={columns} />
      )}
    </div>
  );
}
